'use client'

import * as React from 'react'
import * as SwitchPrimitives from '@radix-ui/react-switch'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/classMerge'
import { Skeleton } from './skeleton'

const switchVariants = cva(
  [
    "peer inline-flex relative shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent",
    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:cursor-not-allowed disabled:opacity-50"
  ].join(" "),
  {
    variants: {
      size: {
        default: "h-5 w-11",
        sm: "h-4 w-8", 
        lg: "h-6 w-12"
      },
      variant: {
        default: [
          "data-[state=checked]:bg-primary-500 data-[state=unchecked]:bg-gray-600",
          "data-[state=checked]:hover:bg-primary-400 data-[state=unchecked]:hover:bg-gray-500",
          "data-[state=checked]:border-primary-500 data-[state=unchecked]:border-gray-600"
        ].join(" "),
        orange: [
          "data-[state=checked]:bg-orange-500 data-[state=unchecked]:bg-gray-800", 
          "data-[state=checked]:hover:bg-orange-400 data-[state=unchecked]:hover:bg-gray-500",
          "focus:ring-2 focus:ring-orange-700"
        ].join(" "),
        custom: [
          "data-[state=checked]:bg-primary-500 data-[state=unchecked]:bg-[#35373B]",
          "data-[state=checked]:hover:bg-[#f68d59] data-[state=unchecked]:hover:bg-[#47494e]",
          "data-[state=checked]:border-[#F37A3C] data-[state=unchecked]:border-[#35373B]"
        ].join(" ")
      }
    },
    defaultVariants: {
      size: "default",
      variant: "default"
    }
  }
)

const switchThumbVariants = cva(
  [
    "pointer-events-none block rounded-full bg-white shadow-lg ring-0",
    "transition-transform border-2 border-white"
  ].join(" "),
  {
    variants: {
      size: {
        default: "h-4 w-4 data-[state=checked]:translate-x-6 data-[state=unchecked]:translate-x-0",
        sm: "h-3 w-3 data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0",
        lg: "h-5 w-5 data-[state=checked]:translate-x-6 data-[state=unchecked]:translate-x-0"
      }
    },
    defaultVariants: {
      size: "default"
    }
  }
)

interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
    VariantProps<typeof switchVariants> {
  skeleton?: boolean
  yesLabel?: string
  noLabel?: string
  showLabels?: boolean
  uncheckedBgColor?: string
}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ 
  className, 
  size = "default",
  variant = "default", 
  skeleton = false, 
  yesLabel = 'Yes', 
  noLabel = 'No', 
  showLabels = false, 
  uncheckedBgColor,
  disabled,
  checked,
  ...props 
}, ref) => {
  // Skeleton state
  if (skeleton) {
    return (
      <Skeleton className={cn(
        "rounded-full bg-muted opacity-40",
        size === "sm" && "h-4 w-8",
        size === "default" && "h-5 w-11", 
        size === "lg" && "h-6 w-12"
      )} />
    )
  }

  // Apply custom background color if provided
  const customStyle = uncheckedBgColor ? {
    '--unchecked-bg': uncheckedBgColor
  } as React.CSSProperties : undefined

  return (
    <SwitchPrimitives.Root
      className={cn(
        switchVariants({ size, variant }), 
        uncheckedBgColor && "data-[state=unchecked]:bg-[var(--unchecked-bg)]",
        className
      )}
      style={customStyle}
      ref={ref}
      disabled={disabled}
      checked={checked}
      {...props}
    >
      {/* Labels - only show on default size or larger */}
      {showLabels && size !== "sm" && (
        <>
          {/* Yes Label */}
          <span
            className={cn(
              "absolute left-1 text-xs font-medium transition-opacity duration-200",
              "select-none pointer-events-none z-10",
              checked
                ? "opacity-100 text-white"
                : "opacity-0"
            )}
            aria-hidden="true"
          >
            {yesLabel}
          </span>
          
          {/* No Label */}
          <span
            className={cn(
              "absolute right-1.5 text-xs font-medium transition-opacity duration-200",
              "select-none pointer-events-none z-10", 
              !checked
                ? "opacity-100 text-white"
                : "opacity-0"
            )}
            aria-hidden="true"
          >
            {noLabel}
          </span>
        </>
      )}
      
      <SwitchPrimitives.Thumb
        className={switchThumbVariants({ size })}
      />
    </SwitchPrimitives.Root>
  )
})

Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch, switchVariants }